import { capitalize, isEmpty, isFunction } from "lodash";
import React, { useMemo } from "react";
import { AssetEsDto } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { CellRenderers } from "utils/tableUtils";
import { CopyContent } from "views/Findings/FindingDetails/FindingDetailsSourceInfo";
import { renderBooleanValues } from "views/Findings/FindingsListV3";

export const formatCloudService = (cloudService: string): string => {
  if (!cloudService) return "";
  if (
    [
      "LAMBDA",
      "CONTAINER",
      "BUCKET",
      "SERVERLESS",
      "KUBERNETES_CLUSTER",
      "API_GATEWAY",
      "DATABASE",
    ].includes(cloudService)
  ) {
    return cloudService
      .split("_")
      .map((word) => capitalize(word))
      .join(" ");
  }
  return cloudService;
};

export const useAssetCloudResourceFields = (assetDetails: AssetEsDto) => {
  const cloudResourceFields = useMemo(() => {
    const cloudDetails = {
      ...assetDetails.cloudResourceProperties,
      name: assetDetails?.name,
    };

    const subSectionFields =
      CLOUD_RESOURCE_SUB_SECTION_MAP[
        cloudDetails?.cloudService as keyof typeof CLOUD_RESOURCE_SUB_SECTION_MAP
      ];
    const cloudService = formatCloudService(cloudDetails?.cloudService?.[0]);
    const fields = [
      {
        label: "Cloud Resource",
        name: "cloudService",
        containerClassName: "flex-column",
        formatter: () =>
          cloudDetails.cloudService
            ? cloudService
            : CellRenderers.VALUE_OR_NA(),
        valueClassName: "break-content",
      },
      {
        label: "Publicly Accessible",
        name: "publiclyAccessible",
        formatter: () =>
          cloudDetails.publiclyAccessible != null
            ? renderBooleanValues(cloudDetails.publiclyAccessible)
            : CellRenderers.VALUE_OR_NA(),
      },
      {
        label: "Region",
        name: "region",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(cloudDetails.region),
      },
      {
        label: "Runtime",
        name: "runtime",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(cloudDetails.runtime),
      },
      {
        label: "Architecture",
        name: "architecture",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(cloudDetails.architecture),
      },
      {
        label: "VPC ID",
        name: "vpcId",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(cloudDetails.vpcId),
      },
      {
        label: "ARN",
        name: "arn",
        containerClassName: "flex-column",
        formatter: () => formatArrayField(assetDetails.arn),
        extra: !isEmpty(assetDetails.arn) ? (
          <CopyContent content={assetDetails.arn.join(", ")} fieldLabel="ARN" />
        ) : null,
      },
      ...(cloudDetails?.cloudService && isFunction(subSectionFields)
        ? [
            {
              label: `${cloudService} Details`,
              name: "header1",
            },
            ...subSectionFields(cloudDetails),
          ]
        : []),
    ];
    return fields.map((cloudResourceField) => ({
      ...cloudResourceField,
      disabledPin: true,
    }));
  }, [assetDetails]);
  return cloudResourceFields;
};

const getLambdaDetails = (cloudDetails: any) => [
  {
    label: "Role",
    name: "role",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.role),
  },
  {
    label: "Version",
    name: "version",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.version),
  },
];

const getRDSDetails = (cloudDetails: any) => [
  {
    label: "Storage Type",
    name: "storageType",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.storageType),
  },
  {
    label: "Engine Version",
    name: "engineVersion",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.engineVersion),
  },
  {
    label: "Engine",
    name: "engine",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.engine),
  },
];

const getEC2Details = (cloudDetails: any) => [
  {
    label: "Instance Status",
    name: "instanceStatus",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.instanceStatus),
  },
  {
    label: "Subnet IDs",
    name: "subnetIds",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.subnetIds),
  },
];

const getS3Details = (cloudDetails: any) => [
  {
    label: "Name",
    name: "name",
    containerClassName: "flex-column",
    formatter: () =>
      cloudDetails.name ? cloudDetails.name : CellRenderers.VALUE_OR_NA(),
  },
];

const getContainerDetails = (cloudDetails: any) => [
  {
    label: "Image Name",
    name: "imageNames",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.imageNames),
  },
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.nativeTypes),
  },
];

const getBucketDetails = (cloudDetails: any) => [
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.nativeTypes),
  },
];

const getServerlessDetails = (cloudDetails: any) => [
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.nativeTypes),
  },
];

const getKubernetesClusterDetails = (cloudDetails: any) => [
  {
    label: "Kubernetes Flavour",
    name: "kubernetesFlavour",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.kubernetesFlavor),
  },
  {
    label: "Version",
    name: "version",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.version),
  },
  {
    label: "Authentication Mode",
    name: "authenticationMode",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.authenticationMode),
  },
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.nativeTypes),
  },
];

const getApiGatewayDetails = (cloudDetails: any) => [
  {
    label: "Native Type",
    name: "nativeTypes",
    containerClassName: "flex-column",
    formatter: () => formatArrayField(cloudDetails.nativeTypes),
  },
];

// SUB_SECTION_MAP acts as the cloud resource field map for dynamic field rendering in AddManualAssets
export const CLOUD_RESOURCE_SUB_SECTION_MAP = {
  LAMBDA: getLambdaDetails,
  EC2: getEC2Details,
  RDS: getRDSDetails,
  S3: getS3Details,
  CONTAINER: getContainerDetails,
  BUCKET: getBucketDetails,
  SERVERLESS: getServerlessDetails,
  KUBERNETES_CLUSTER: getKubernetesClusterDetails,
  API_GATEWAY: getApiGatewayDetails,
};

export const CLOUD_RESOURCE_OPTIONS = Object.keys(
  CLOUD_RESOURCE_SUB_SECTION_MAP,
);
